import { YYYYMMDD } from "@/helpers/dateHelper";
import { groupBy } from "@/helpers/groupByHelper";
import { DirectionEnum, ModeOfTravelEnum } from "@/models/enums";
import { PassengerState } from "@/models/www/store/BookingState";
import { BookingItem, BookingSummary, BookingSummarySection } from "@/models/www/store/BookingSummary";
import { useBookingSummaryStore } from "@/store/basket";
import { useBookingStore } from "@/store/booking";
import { useCampaignStore } from "@/store/campaign";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import useBookingSummary from "./useBookingSummary";

const useBookingSummaryEffect = () => {
    const { t } = useI18n();

    const bookingStore = useBookingStore();
    const bookingSummaryStore = useBookingSummaryStore();
    const bookingSummary = useBookingSummary();
    const campaignStore = useCampaignStore();

    const getBookingSummary = () => {
        return {
            bookingNumber: bookingStore.getBooking.bookingNumber,
            modeOfTravel: t(`bookingType.${ModeOfTravelEnum[bookingStore.getBooking.modeOfTravel]}`),
            serviceType: bookingStore.getBooking.tripType === DirectionEnum.Outward ? t("serviceType.oneWay") : t("serviceType.return"),
            passengers: [...getPassengerItems(DirectionEnum.Outward)],
            extras: bookingSummaryStore.getBookingSummary.extras.length > 0 ? bookingSummaryStore.getBookingSummary.extras : [] as BookingItem[],
            bookingSections: getBookingSections(),
            campaignName: campaignStore.getState.name
        } as BookingSummary;
    }

    const getBookingSections = (): BookingSummarySection[] => {
        const sections: BookingSummarySection[] = [];

        const trips = bookingStore.getBooking.tripType === DirectionEnum.Return ? [DirectionEnum.Outward, DirectionEnum.Return] : [DirectionEnum.Outward];
        const storeSections = bookingSummaryStore.getBookingSummarySections;

        trips.forEach((d) => {
            const route = bookingStore.getRoute(d);

            //check if we got sections saved in summary store
            //and if the departure exists in state
            //if true then reuse it 
            const section = storeSections?.find(x => x.route === route);  
                 
            sections.push({
                travelType: bookingStore.getBooking.modeOfTravel,
                items: [] as BookingItem[],
                departureDate: YYYYMMDD(bookingStore.getDate(d) ?? new Date()),
                icon: bookingSummary.getIconNames(d),
                route: route,
                routeText: bookingSummary.getRouteTranslations(route, bookingStore.getBooking.modeOfTravel),
                price: 0 
            } as BookingSummarySection)
        })

        return sections;
    }

    const getPassengerItems = (direction: DirectionEnum): BookingItem[] => {
        const passengers = groupBy(bookingStore.getPassengers(direction), "type");
        const passengerItems = [];

        for (const key in passengers) {
            const resource = passengers[key] as PassengerState[];

            passengerItems.push({
                text: t(`passenger.${resource[0].type}`, resource.length),
                amount: resource.length,
                type: resource[0].type,
            } as BookingItem);
        }

        return passengerItems.sort(sortPassengers);
    }

    const order = ["A", "Y", "C", "I", "D"];
    const sortPassengers = (a: BookingItem, b: BookingItem) => {
        const indexA = order.indexOf(a.type);
        const indexB = order.indexOf(b.type);

        if(indexA > -1 && indexB > -1) {
            if(indexA > indexB) {
                return 1
            }
            else if(indexB > indexA){
                return -1
            }
        }

        return 0;
    }

    const calculateProperties = computed(() => {
        const tripType = bookingStore.getBooking.tripType;
        const modeOfTravel = bookingStore.getBooking.modeOfTravel;
        const routes = [DirectionEnum.Outward, DirectionEnum.Return].map(d => bookingStore.getRoute(d));
        const passengers = [DirectionEnum.Outward, DirectionEnum.Return].map(d => bookingStore.getPassengers(d).length);
        const campaign = campaignStore.getState.name;
        const dates = [DirectionEnum.Outward, DirectionEnum.Return].map(d => bookingStore.getDate(d));

        return [tripType, modeOfTravel, passengers, campaign, dates, routes];
    });

    const unsubscribe = ref<any[]>([]);
    onMounted(() => {
        unsubscribe.value.push(watch(calculateProperties, () => {
                bookingSummaryStore.setBookingSummary(getBookingSummary());
            },
            { immediate: true }
        ));
    });

    onBeforeUnmount(() => {
        unsubscribe.value.forEach(v => v());
    });
}

export default useBookingSummaryEffect;